import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {EditorState} from '@codemirror/state';
import {EditorView, keymap, lineNumbers, highlightActiveLine} from '@codemirror/view';
import {defaultKeymap, history, historyKeymap, indentWithTab} from '@codemirror/commands';
import {syntaxHighlighting, HighlightStyle, foldGutter, bracketMatching} from '@codemirror/language';
import {closeBrackets, closeBracketsKeymap} from '@codemirror/autocomplete';
import {tags as t} from '@lezer/highlight';
import {json} from '@codemirror/lang-json';
import {yaml} from '@codemirror/lang-yaml';
import {xml} from '@codemirror/lang-xml';
import {markdown} from '@codemirror/lang-markdown';
import {html} from '@codemirror/lang-html';

import styles from './CodeEditor.module.scss';

// CSV has no maintained CodeMirror grammar, so it's edited as plain text (no extension).
const LANGUAGE_EXTENSIONS = {
    json: () => [json()],
    yaml: () => [yaml()],
    xml: () => [xml()],
    markdown: () => [markdown()],
    html: () => [html()],
    csv: () => [],
}

// Dracula palette, matching the react-syntax-highlighter theme used
// elsewhere in the project to display code (see the Bash/Python pages).
const draculaHighlightStyle = HighlightStyle.define([
    {tag: t.comment, color: '#6272a4'},
    {tag: [t.propertyName, t.tagName, t.deleted], color: '#ff79c6'},
    {tag: [t.bool, t.number], color: '#bd93f9'},
    {tag: [t.string, t.attributeValue, t.inserted, t.special(t.string)], color: '#50fa7b'},
    {tag: [t.keyword, t.operatorKeyword, t.controlKeyword], color: '#8be9fd'},
    {tag: [t.attributeName, t.className, t.definition(t.propertyName), t.heading], color: '#f1fa8c'},
    {tag: t.invalid, color: '#ff5555'},
])

const draculaTheme = EditorView.theme({
    '&': {backgroundColor: '#282a36', color: '#f8f8f2', height: '100%'},
    '.cm-content': {caretColor: '#f8f8f2'},
    '.cm-gutters': {backgroundColor: '#282a36', color: '#6272a4', border: 'none'},
    '.cm-activeLine, .cm-activeLineGutter': {backgroundColor: 'rgba(255, 255, 255, 0.05)'},
    '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
        backgroundColor: 'rgba(98, 114, 164, 0.5) !important',
    },
}, {dark: true})

function buildExtensions(language, readOnly, onChangeRef, onCursorLineRef) {
    return [
        lineNumbers(),
        history(),
        foldGutter(),
        bracketMatching(),
        closeBrackets(),
        highlightActiveLine(),
        keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...historyKeymap, indentWithTab]),
        syntaxHighlighting(draculaHighlightStyle),
        draculaTheme,
        EditorView.lineWrapping,
        EditorState.readOnly.of(readOnly),
        ...LANGUAGE_EXTENSIONS[language](),
        EditorView.updateListener.of(update => {
            if (update.docChanged && onChangeRef.current) {
                onChangeRef.current(update.state.doc.toString())
            }
            if ((update.docChanged || update.selectionSet) && onCursorLineRef.current) {
                const line = update.state.doc.lineAt(update.state.selection.main.head).number
                onCursorLineRef.current(line)
            }
        }),
    ]
}

CodeEditor.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    onCursorLine: PropTypes.func,
    language: PropTypes.oneOf(Object.keys(LANGUAGE_EXTENSIONS)).isRequired,
    readOnly: PropTypes.bool,
}

export default function CodeEditor({value, onChange, onCursorLine, language, readOnly = false}) {
    const containerRef = useRef()
    const viewRef = useRef()
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const onCursorLineRef = useRef(onCursorLine)
    onCursorLineRef.current = onCursorLine

    useEffect(() => {
        const view = new EditorView({
            parent: containerRef.current,
            state: EditorState.create({
                doc: value,
                extensions: buildExtensions(language, readOnly, onChangeRef, onCursorLineRef),
            }),
        })
        viewRef.current = view

        return () => {
            view.destroy()
            viewRef.current = null
        }
        // Recreated only when language/readOnly change; `value` is synced by the effect below
        // instead of being a dependency here, so typing doesn't tear down and rebuild the view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, readOnly])

    useEffect(() => {
        const view = viewRef.current
        if (!view) return
        const current = view.state.doc.toString()
        if (current !== value) {
            view.dispatch({changes: {from: 0, to: current.length, insert: value}})
        }
    }, [value])

    return <div className={styles.wrapper} ref={containerRef}/>
}
